import React, { useState, useEffect, useMemo } from 'react'

import { pickerTheme } from 'designer/component/system/themes/elementTheme'
import { ThemeProvider } from '@mui/material/styles'

import LocalizationProvider from '@mui/lab/LocalizationProvider'
import thLocale from 'date-fns/locale/th'

import OverwriteFnsBE from './OverwriteFnsBE'
import OverwriteFnsAD from './OverwriteFnsAD'

import Box from '@mui/material/Box'
import DesktopDatePicker from '@mui/lab/DatePicker'

import InputMask from 'react-input-mask'

import classNames from 'classnames/bind'
import './datepicker-custom-styles.css'

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useTimeFormat } from 'contexts/TimeFormatContext'
import { Form } from 'react-bootstrap'
import { updateOnChange } from 'designer/component/control/page/DataControl'
import {
  INVALID_DATE_TIME_VALUE,
  parseDateInput,
} from 'designer/component/validation/dateTime'
dayjs.extend(customParseFormat)

const FORMAT_DATE = 'YYYY-MM-DD'

const DatePickerCustom = ({
  dateAdapter,
  locale,
  value,
  onChange,
  placeholder,
  formatDisplay,
  maskSign,
  isClearable,
  isError,
  readonly,
  calendar,
  year,
  id,
  name,
  isCustomFormat,
  style,
  handleKeyDown = () => {},
  isTable,
  validateInput = false,
  onClose = () => {},
  ...props
}) => {
  let formatDsp = formatDisplay || ''
  let regexAlphabet = /^[a-zA-Z]+$/
  let yearAdd = year || 0
  let { formatDate } = useTimeFormat()

  const formatToMask = (formatDsp = '') => {
    let mask = formatDsp.split('').reduce((items, item) => {
      if (regexAlphabet.test(item)) {
        items += 9
      } else {
        items += item
      }
      return items
    }, '')
    return mask
  }

  let maskInput = useMemo(() => {
    return formatToMask(formatDisplay)
  }, [formatDsp])

  const parseStringDate = (dateString) => {
    return dayjs(dateString).toDate()
  }

  const convertDate = (data, input = false) => {
    if (data) {
      const replaceForm = formatDsp.replace(/B/g, 'Y')
      const date = input && validateInput
        ? parseDateInput(data, formatDsp)
        : dayjs(data, replaceForm)
      if (date?.isValid()) {
        const yearAdd = input && formatDsp.includes('B') ? 543 : 0
        const ceYear = date.year() - yearAdd
        const ceDate = `${ceYear}${date.format('-MM-DD')}`
        return parseStringDate(ceDate)
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  const [dateValue, setDateValue] = useState(value ? convertDate(value) : '')
  const [dateText, setDateText] = useState(
    value ? dayjs(value).add(yearAdd, 'year').format(formatDsp) : '',
  )

  const handleChangeDateText = (e) => {
    let { value } = e.target
    setDateText(value)
    if (!value) {
      setDateValue('')
      if (validateInput && onChange) {
        onChange({})
      }
      return
    }
    let newDate = convertDate(value, true)
    if (newDate) {
      let dateFormat = dayjs(newDate).add(yearAdd, 'year').format(FORMAT_DATE)
      setDateValue(newDate)

      if (onChange) {
        onChange({ value: newDate, format: dateFormat })
      }
    } else if (validateInput) {
      setDateValue('')
      if (onChange) {
        onChange({
          value: INVALID_DATE_TIME_VALUE,
          format: INVALID_DATE_TIME_VALUE,
        })
      }
    }
  }

  const handleChangeDate = (date) => {
    let dateFormat = dayjs(date).add(yearAdd, 'year').format(FORMAT_DATE)
    let dateDisplay = dayjs(date).add(yearAdd, 'year').format(formatDsp)
    setDateValue(date)
    setDateText(dateDisplay)
    if (onChange) {
      onChange({ value: date, format: dateFormat })
    }
  }

  const clearDate = () => {
    setDateValue('')
    setDateText('')
    if (onChange) {
      onChange({})
    }
  }

  useEffect(() => {
    if (value === INVALID_DATE_TIME_VALUE) return
    if (value) {
      setDateText(dayjs(value).add(yearAdd, 'year').format(formatDsp))
      setDateValue(convertDate(dayjs(value).add(yearAdd, 'year')))
    } else {
      setDateText('')
      setDateValue('')
    }
  }, [formatDisplay, calendar, value])

  if (readonly) {
    return isTable ? (
      dateText
    ) : (
      <Form.Control readOnly id={id} name={name} value={dateText} />
    )
  }

  return (
    <LocalizationProvider
      dateAdapter={formatDsp.search('B') > -1 ? OverwriteFnsBE : OverwriteFnsAD}
      locale={locale ?? thLocale}
    >
      <ThemeProvider theme={pickerTheme}>
        <DesktopDatePicker
          id={id}
          name={name}
          views={['year', 'month', 'day']}
          value={dateValue}
          onChange={(newValue) => {
            handleChangeDate(newValue)
          }}
          readOnly={readonly}
          renderInput={({ inputRef, inputProps, InputProps }) => {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <div className="position-relative w-100">
                  <InputMask
                    {...inputProps}
                    style={style}
                    id={`${id}-input`}
                    name={`${name}-input`}
                    value={dateText}
                    onChange={handleChangeDateText}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder ?? formatDsp}
                    mask={maskInput}
                    maskChar={'_'}
                    disabled={readonly}
                    readOnly={readonly}
                  >
                    {(inputMaskProps) => {
                      return (
                        <input
                          className={classNames('form-control', {
                            'input-date pr': isClearable,
                            'is-invalid': isError,
                          })}
                          ref={inputRef}
                          {...inputMaskProps}
                        />
                      )
                    }}
                  </InputMask>

                  {isClearable && dateText && (
                    <button className="clear-btn" onClick={() => clearDate()}>
                      <i
                        className={classNames('fas fa-times clearable-btn', {
                          error: isError,
                        })}
                      />
                    </button>
                  )}
                </div>

                {InputProps?.endAdornment}
              </Box>
            )
          }}
          onClose={onClose}
          {...props}
        />
      </ThemeProvider>
    </LocalizationProvider>
  )
}

export default DatePickerCustom
